import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, ArrowRight } from 'lucide-react';

const services = [
  'Fumigation & Pest Control',
  'Sanitary Disposal Services',
  'Hazards & Industrial Waste',
  'General Waste Management',
  'Landscaping Services',
];

const quickLinks = [
  { to: '/',         label: 'Home'        },
  { to: '/about',    label: 'About Us'    },
  { to: '/services', label: 'Our Services'},
  { to: '/contact',  label: 'Contact Us'  },
];

export default function Footer() {
  return (
    <footer style={{ background: '#001a5c' }} className="text-white relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute top-0 right-0 w-80 h-80 bg-brand-blue/5 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 w-64 h-64 bg-brand-red/4 rounded-full blur-3xl" />

      {/* Top bar — glass on dark */}
      <div
        className="relative z-10 border-b"
        style={{
          background:           'rgba(2,174,242,0.90)',
          backdropFilter:       'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderColor:          'rgba(255,255,255,0.08)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-black/10 rounded-full flex items-center justify-center">
              <Phone size={14} className="text-gray-900" />
            </div>
            <span className="text-sm font-inter text-gray-900 font-medium">24/7 Emergency Response — Call us anytime</span>
          </div>
          <a
            href="tel:0722148999"
            className="text-gray-900 font-black text-lg font-poppins tracking-wide hover:text-black transition-colors cursor-pointer"
          >
            0722 148 999
          </a>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-5 group cursor-pointer">
              <img src="/images/logo.jpg" alt="Lidav" className="h-11 w-11 rounded-full object-contain ring-2 ring-white/20 group-hover:ring-brand-red transition-all duration-300" />
              <div>
                <div className="text-white font-bold font-poppins text-sm tracking-wide">LIDAV ENTERPRISES</div>
                <div className="text-white/40 text-[9px] tracking-widest uppercase">Limited</div>
              </div>
            </Link>
            <p className="text-white/55 text-sm font-inter leading-relaxed mb-6">
              Protecting Health, Environment &amp; Spaces. Kenya's trusted partner for professional facilities management and environmental services since 2013.
            </p>
            <div className="flex items-center gap-2.5">
              {[
                { icon: Instagram, href: 'https://www.instagram.com/lidaventerpriseltd', label: 'Instagram' },
                { icon: Facebook,  href: '#', label: 'Facebook'  },
                { icon: Twitter,   href: '#', label: 'Twitter'   },
                { icon: Linkedin,  href: '#', label: 'LinkedIn'  },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:-translate-y-1 hover:bg-brand-red cursor-pointer"
                  style={{
                    background:           'rgba(255,255,255,0.08)',
                    backdropFilter:       'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    border:               '1px solid rgba(255,255,255,0.08)',
                  }}
                  aria-label={label}
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold font-poppins text-xs uppercase tracking-widest mb-5 after:content-[''] after:block after:w-8 after:h-0.5 after:bg-brand-red after:mt-2">
              Our Services
            </h4>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s}>
                  <Link
                    to="/services"
                    className="flex items-center gap-2 text-white/55 hover:text-brand-red text-sm font-inter transition-colors group cursor-pointer"
                  >
                    <ArrowRight size={11} className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-200" />
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold font-poppins text-xs uppercase tracking-widest mb-5 after:content-[''] after:block after:w-8 after:h-0.5 after:bg-brand-red after:mt-2">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="flex items-center gap-2 text-white/55 hover:text-brand-red text-sm font-inter transition-colors group cursor-pointer"
                  >
                    <ArrowRight size={11} className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-200" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold font-poppins text-xs uppercase tracking-widest mb-5 after:content-[''] after:block after:w-8 after:h-0.5 after:bg-brand-red after:mt-2">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-brand-blue/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin size={14} className="text-brand-red" />
                </div>
                <div>
                  <p className="text-white/85 text-sm font-inter">Utalii House — Uhuru Highway</p>
                  <p className="text-white/45 text-xs font-inter">Nairobi, Kenya</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 bg-brand-blue/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone size={14} className="text-brand-red" />
                </div>
                <a href="tel:0722148999" className="text-white/85 hover:text-brand-red text-sm font-inter transition-colors cursor-pointer">
                  0722 148 999
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 bg-brand-blue/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail size={14} className="text-brand-red" />
                </div>
                <a href="mailto:info@lidaventerprises.com" className="text-white/85 hover:text-brand-red text-sm font-inter transition-colors break-all cursor-pointer">
                  info@lidaventerprises.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 bg-brand-blue/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Instagram size={14} className="text-brand-red" />
                </div>
                <a
                  href="https://www.instagram.com/lidaventerpriseltd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/85 hover:text-brand-red text-sm font-inter transition-colors cursor-pointer"
                >
                  @lidaventerpriseltd
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div
        className="relative z-10 border-t py-5"
        style={{ borderColor: 'rgba(255,255,255,0.07)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/35 font-inter">
          <p>© {new Date().getFullYear()} Lidav Enterprises Limited. All rights reserved.</p>
          <p>Protecting Health, Environment &amp; Spaces — Nairobi, Kenya</p>
        </div>
      </div>
    </footer>
  );
}
